import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@istari-frontend/ui";
import { Tags } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

import { useControlTags } from "~/control-tags/data-source/use-control-tags";
import { ControlTagSummary } from "~/control-tags/model/control-tag";
import { ControlTagChip } from "~/control-tags/ui/control-tag-chip";
import { ControlTagCommandOptions } from "~/control-tags/ui/control-tag-command-options";
import { OverlappingTagList } from "~/control-tags/ui/overlapping-tag-list";

interface ControlTagMultiSelectProps {
  id?: string;
  label?: ReactNode;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  // Renders selected tags as stacked circles in a slim trigger instead of full-width chips.
  compact?: boolean;
}

// On a catalog outage the tag names can't be resolved, but hiding the assignment would make a
// restricted integration read as unrestricted — keep the count visible.
const unavailableLabel = (assignedCount: number): string =>
  assignedCount === 0
    ? "Control tags unavailable"
    : `${assignedCount} ${assignedCount === 1 ? "tag" : "tags"} assigned — names unavailable`;

export function ControlTagMultiSelect({
  id,
  label = "Control tags",
  value,
  onChange,
  disabled,
  compact,
}: ControlTagMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const controlTagsQuery = useControlTags();
  const catalog = controlTagsQuery.data ?? [];
  // Only active, internally-managed tags are offered as options, but selected chips resolve
  // against the whole catalog: an assignment can legitimately hold an inactive or
  // externally-managed tag, and hiding its chip would under-represent the real access rule.
  const availableTags = catalog.filter((tag) => tag.status === "active" && !tag.isExternallyManaged);
  const selectedTags = value
    .map((tagId) => catalog.find((tag) => tag.id === tagId))
    .filter((tag): tag is ControlTagSummary => Boolean(tag));

  const toggle = (tagId: string) => {
    onChange(value.includes(tagId) ? value.filter((selectedId) => selectedId !== tagId) : [...value, tagId]);
  };

  const triggerDisabled = disabled || controlTagsQuery.isPending || controlTagsQuery.isError;
  const emptyLabel = (
    <span className="text-muted-foreground flex items-center gap-1 text-sm">
      <Tags className="size-3.5" />
      {controlTagsQuery.isError ? unavailableLabel(value.length) : "Add tags…"}
    </span>
  );

  return (
    <div className="space-y-2">
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={triggerDisabled}>
          {compact ? (
            <Button
              id={id}
              type="button"
              disabled={triggerDisabled}
              variant="outline"
              size="sm"
              className="w-fit gap-1.5 font-normal"
              aria-label="Change control tags"
            >
              {selectedTags.length > 0 ? <OverlappingTagList tags={selectedTags} showTooltip={false} /> : emptyLabel}
            </Button>
          ) : (
            <Button
              id={id}
              type="button"
              disabled={triggerDisabled}
              variant="outline"
              className="h-auto min-h-9 w-full justify-start gap-1.5 px-3 py-1.5 font-normal"
              aria-label="Change control tags"
            >
              {selectedTags.length > 0
                ? selectedTags.map((tag) => <ControlTagChip key={tag.id} tag={tag} />)
                : emptyLabel}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className={compact ? "w-64 p-0" : "w-[--radix-popover-trigger-width] p-0"}
          align="start"
          sideOffset={6}
        >
          <Command>
            <CommandInput placeholder="Search tags…" />
            <CommandList className="max-h-64 overflow-y-auto overscroll-contain">
              <CommandEmpty>No tags found.</CommandEmpty>
              <ControlTagCommandOptions tags={availableTags} selectedIds={value} onToggle={(tag) => toggle(tag.id)} />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
